/**
 * AppViewport — wraps an app's content and applies the active ViewportMode.
 *
 * - In 'padded' mode the scroll container that actually scrolls (Container
 *   leaf / Stack / Shell content) reserves --fab-clearance at its bottom as a
 *   page-coloured footer gap, so the FAB never covers the last item at max
 *   scroll. When `aspectRatio` is set the available area shrinks by
 *   --fab-clearance and content is letterboxed/pillarboxed above the FAB (so
 *   games don't get clipped).
 * - In 'full' mode the FAB overlays the viewport — useful for fullscreen games
 *   or apps that already manage their own bottom inset.
 *
 * The mode comes from the surrounding ViewportModeProvider. A toggle item is
 * automatically added to the FAB by RootWrapper.
 */

import type { CSSProperties, ReactNode } from 'react';
import { useViewportMode } from '../../contexts/ViewportModeContext';
import './AppViewport.css';

export interface AppViewportProps {
  children: ReactNode;
  /**
   * Optional aspect ratio (width / height) for fixed-canvas apps such as
   * games. When set, the content is centered and letterboxed/pillarboxed to
   * preserve this ratio inside the available area.
   */
  aspectRatio?: number;
  /** Optional className applied to the outer viewport element. */
  className?: string;
  /** Optional inline style applied to the outer viewport element. */
  style?: CSSProperties;
}

export function AppViewport({ children, aspectRatio, className, style }: AppViewportProps) {
  const { mode } = useViewportMode();
  const hasAspect = typeof aspectRatio === 'number' && aspectRatio > 0;

  const mergedStyle: CSSProperties & Record<string, string | number> = { ...style };
  if (hasAspect) {
    mergedStyle['--app-aspect-num'] = aspectRatio;
  }

  const classes = ['room-app-viewport'];
  if (className) classes.push(className);

  if (hasAspect) {
    return (
      <div
        className={classes.join(' ')}
        data-mode={mode}
        data-aspect="true"
        style={mergedStyle}
      >
        <div className="room-app-viewport-aspect-box">{children}</div>
      </div>
    );
  }

  return (
    <div className={classes.join(' ')} data-mode={mode} style={mergedStyle}>
      {children}
    </div>
  );
}
